import { apiRequest, apiRoutes } from '../../api';
import React, { useState, useEffect, useCallback } from 'react';

const STATUS_LABELS = {
    PEND:        { text: 'در انتظار پاسخ', color: '#f57c00', bg: '#fff3e0' },
    NEGOTIATING: { text: 'در حال مذاکره', color: '#1565c0', bg: '#e3f2fd' },
    ACCEPTED:    { text: 'پذیرفته شده',   color: '#2e7d32', bg: '#e8f5e9' },
    REJECTED:    { text: 'رد شده',         color: '#c62828', bg: '#ffebee' },
    COMPLETED:   { text: 'نهایی شده',      color: '#2e7d32', bg: '#e8f5e9' },
    CANCELLED:   { text: 'لغو شده',        color: '#757575', bg: '#f5f5f5' },
};

// New API: offer status values are lowercase
const OFFER_STATUS_LABELS = {
    pending_response:  { text: 'در انتظار پاسخ شما', color: '#f57c00', bg: '#fff3e0' },
    changes_requested: { text: 'تغییرات درخواست شده', color: '#1565c0', bg: '#e3f2fd' },
    accepted:          { text: 'پذیرفته شده',          color: '#2e7d32', bg: '#e8f5e9' },
    rejected:          { text: 'رد شده',               color: '#c62828', bg: '#ffebee' },
    withdrawn:         { text: 'پس گرفته شده',          color: '#757575', bg: '#f5f5f5' },
    expired:           { text: 'منقضی شده',             color: '#757575', bg: '#f5f5f5' },
    cancelled:         { text: 'لغو شده',               color: '#757575', bg: '#f5f5f5' },
};

// ── Offers sub-panel (owner view) ────────────────────────────────────────────
// Fetches the offer for this reservation from GET /dealbridge/offers/
// Actions target the current_revision via POST /dealbridge/offer-revisions/{id}/{action}/
function OffersPanel({ reservationId, token, onOfferActioned }) {
    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [rejectNote, setRejectNote] = useState('');
    const [showRejectForm, setShowRejectForm] = useState(false);

    const fetchOffer = useCallback(async () => {
        try {
            const res = await apiRequest(apiRoutes.dealbridge.offers, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                const all = Array.isArray(data) ? data : (data.results ?? []);
                const found = all.find(o => o.reservation === reservationId);
                setOffer(found || null);
            }
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [reservationId, token]);

    useEffect(() => { fetchOffer(); }, [fetchOffer]);

    const doRevisionAction = async (revisionId, action, body = {}) => {
        setActionLoading(true);
        try {
            const res = await apiRequest(
                apiRoutes.dealbridge.offerRevisionAction(revisionId, action),
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(body),
                }
            );
            if (res.ok) {
                await fetchOffer();
                onOfferActioned?.();
            } else {
                const err = await res.json();
                alert(err.detail || 'خطا در انجام عملیات');
            }
        } catch { alert('خطا در ارتباط با سرور'); }
        finally { setActionLoading(false); setShowRejectForm(false); setRejectNote(''); }
    };

    if (loading || !offer) return null;

    const revision = offer.current_revision;
    const badge = OFFER_STATUS_LABELS[offer.status] || { text: offer.status, color: '#333', bg: '#eee' };
    // Owner can act when current revision was submitted by the buyer
    const canAct = revision &&
        revision.status === 'submitted' &&
        revision.created_by_role === 'buyer' &&
        offer.status === 'pending_response';

    return (
        <div className="offers-panel">
            <div className="offers-panel-title"> پیشنهاد قیمت</div>
            <div className="offer-card">
                <div className="offer-card-top">
                    <span className="offer-badge" style={{ background: badge.bg, color: badge.color }}>
                        {badge.text}
                    </span>
                </div>

                {revision && (
                    <div className="offer-details">
                        {revision.asking_price && (
                            <div className="offer-row">
                                <span>قیمت پیشنهادی:</span>
                                <span>{Number(revision.asking_price).toLocaleString('fa-IR')} تومان</span>
                            </div>
                        )}
                        {revision.deposit && (
                            <div className="offer-row">
                                <span>ودیعه:</span>
                                <span>{Number(revision.deposit).toLocaleString('fa-IR')} تومان</span>
                            </div>
                        )}
                        {revision.monthly_rent && (
                            <div className="offer-row">
                                <span>اجاره ماهانه:</span>
                                <span>{Number(revision.monthly_rent).toLocaleString('fa-IR')} تومان</span>
                            </div>
                        )}
                        {revision.payment_phases?.length > 0 && (
                            <div className="offer-phases">
                                <span>مراحل پرداخت:</span>
                                {revision.payment_phases.map((ph, i) => (
                                    <div key={i} className="offer-phase-row">
                                        {ph.label && `${ph.label} — `}{ph.date}: {Number(ph.amount).toLocaleString()} تومان
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {canAct && (
                    <div className="offer-actions">
                        <button className="res-btn res-btn-accept" disabled={actionLoading}
                            onClick={() => doRevisionAction(revision.id, 'accept')}>
                            {actionLoading ? '...' : 'پذیرش پیشنهاد'}
                        </button>
                        <button className="res-btn res-btn-reject" disabled={actionLoading}
                            onClick={() => setShowRejectForm(true)}>
                            رد پیشنهاد
                        </button>
                    </div>
                )}

                {showRejectForm && (
                    <div className="reservation-reject-form">
                        <textarea className="reservation-reject-input" rows={2}
                            placeholder="دلیل رد (اختیاری)" value={rejectNote}
                            onChange={e => setRejectNote(e.target.value)} />
                        <div className="reservation-reject-actions">
                            <button className="res-btn res-btn-reject" disabled={actionLoading}
                                onClick={() => doRevisionAction(revision.id, 'reject', {})}>
                                {actionLoading ? '...' : 'تأیید رد'}
                            </button>
                            <button className="res-btn res-btn-cancel"
                                onClick={() => { setShowRejectForm(false); setRejectNote(''); }}>
                                انصراف
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Shows pending reservation requests for a specific listing.
 * Rendered inside the owner's listing card in PropertyWizard dashboard.
 *
 * Props:
 *   listingId  – the listing's id
 *   token      – JWT access token
 */
export default function ReservationRequestsPanel({ listingId, token }) {
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(null); // reservation id being acted on
    const [rejectNote, setRejectNote] = useState('');
    const [rejectingId, setRejectingId] = useState(null); // id of reservation showing reject form

    const fetchReservations = useCallback(async () => {
        if (!token || !listingId) return;
        try {
            const res = await apiRequest(apiRoutes.dealbridge.reservations, {
                params: { scope: 'received' },
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                const all = Array.isArray(data) ? data : data.results ?? [];
                // Filter to this listing only
                const filtered = all.filter(
                    (r) => r.listing === listingId || r.listing_info?.id === listingId
                );
                setReservations(filtered);
            }
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [listingId, token]);

    useEffect(() => { fetchReservations(); }, [fetchReservations]);

    const doAction = async (reservationId, action, body = {}) => {
        setActionLoading(reservationId);
        try {
            const res = await apiRequest(apiRoutes.dealbridge.reservationAction(reservationId, action), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(body),
            });
            if (res.ok) {
                await fetchReservations();
            } else {
                const err = await res.json();
                alert(err.detail || 'خطا در انجام عملیات');
            }
        } catch {
            alert('خطا در ارتباط با سرور');
        } finally {
            setActionLoading(null);
            setRejectingId(null);
            setRejectNote('');
        }
    };

    // Only show the panel if there are any reservations
    const active = reservations.filter(
        (r) => !['COMPLETED', 'CANCELLED', 'REJECTED'].includes(r.status)
    );
    const past = reservations.filter(
        (r) => ['COMPLETED', 'CANCELLED', 'REJECTED'].includes(r.status)
    );

    if (loading) return null;
    if (reservations.length === 0) return null;

    return (
        <div className="reservation-panel">
            <div className="reservation-panel-title">
                درخواست‌های رزرو
                {active.length > 0 && (
                    <span className="reservation-panel-badge">{active.length}</span>
                )}
            </div>

            {active.map((r) => {
                const badge = STATUS_LABELS[r.status] || { text: r.status, color: '#333', bg: '#eee' };
                const busy = actionLoading === r.id;
                const buyerName = r.buyer?.full_name || r.buyer?.username || `خریدار #${r.id.slice(0, 6)}`;

                return (
                    <div key={r.id} className="reservation-card">
                        <div className="reservation-card-top">
                            <span className="reservation-buyer">{buyerName}</span>
                            <span
                                className="reservation-status-badge"
                                style={{ background: badge.bg, color: badge.color }}
                            >
                                {badge.text}
                            </span>
                        </div>

                        {r.note && (
                            <p className="reservation-note">یادداشت: {r.note}</p>
                        )}

                        <div className="reservation-actions">
                            {/* While pending, the owner can accept or reject. */}
                            {r.status === 'PEND' && (
                                <>
                                    <button
                                        className="res-btn res-btn-accept"
                                        disabled={busy}
                                        onClick={() => doAction(r.id, 'accept')}
                                    >
                                        {busy ? '...' : 'پذیرش و شروع مذاکره'}
                                    </button>
                                    <button
                                        className="res-btn res-btn-reject"
                                        disabled={busy}
                                        onClick={() => setRejectingId(r.id)}
                                    >
                                        رد درخواست
                                    </button>
                                </>
                            )}

                            {/* During negotiation the owner can reject; offer acceptance is handled in OffersPanel. */}
                            {r.status === 'NEGOTIATING' && (
                                <>
                                    <button
                                        className="res-btn res-btn-reject"
                                        disabled={busy}
                                        onClick={() => setRejectingId(r.id)}
                                    >
                                        رد درخواست
                                    </button>
                                </>
                            )}

                            {/* ACCEPTED — waiting for buyer to complete */}
                            {r.status === 'ACCEPTED' && (
                                <span className="res-waiting">در انتظار تأیید نهایی خریدار</span>
                            )}
                        </div>

                        {/* Inline reject form */}
                        {rejectingId === r.id && (
                            <div className="reservation-reject-form">
                                <textarea
                                    className="reservation-reject-input"
                                    placeholder="دلیل رد (اختیاری)"
                                    value={rejectNote}
                                    onChange={(e) => setRejectNote(e.target.value)}
                                    rows={2}
                                />
                                <div className="reservation-reject-actions">
                                    <button
                                        className="res-btn res-btn-reject"
                                        disabled={busy}
                                        onClick={() => doAction(r.id, 'reject', { rejection_note: rejectNote })}
                                    >
                                        {busy ? '...' : 'تأیید رد'}
                                    </button>
                                    <button
                                        className="res-btn res-btn-cancel"
                                        onClick={() => { setRejectingId(null); setRejectNote(''); }}
                                    >
                                        انصراف
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Offers panel — visible when negotiating or accepted */}
                        {['NEGOTIATING', 'ACCEPTED'].includes(r.status) && (
                            <OffersPanel
                                reservationId={r.id}
                                token={token}
                                onOfferActioned={fetchReservations}
                            />
                        )}
                    </div>
                );
            })}

            {/* Collapsed past reservations */}
            {past.length > 0 && (
                <details className="reservation-past">
                    <summary>درخواست‌های قبلی ({past.length})</summary>
                    {past.map((r) => {
                        const badge = STATUS_LABELS[r.status] || { text: r.status, color: '#333', bg: '#eee' };
                        const buyerName = r.buyer?.full_name || r.buyer?.username || `خریدار #${r.id.slice(0, 6)}`;
                        return (
                            <div key={r.id} className="reservation-card reservation-card-past">
                                <span className="reservation-buyer">{buyerName}</span>
                                <span
                                    className="reservation-status-badge"
                                    style={{ background: badge.bg, color: badge.color }}
                                >
                                    {badge.text}
                                </span>
                            </div>
                        );
                    })}
                </details>
            )}
        </div>
    );
}
